// Injects the non-essential trackers.
//
// GTM loads for EVERY visitor so Google Consent Mode v2 can work: the Google
// tags in the container (GA4 / Ads) read the "denied" default set in <head>
// (src/components/consent/consent-mode.js) and send cookieless, non-identifying
// pings until the visitor accepts, at which point consent-context flips the
// signals to "granted" — keeping refusals visible (modeled) in GA4. Non-Google
// tags in the container (e.g. the Meta Pixel) ignore Consent Mode and must be
// gated by a consent-based trigger in GTM itself.
//
// HubSpot is NOT a Consent Mode participant (it drops its own cookies), so it
// stays strictly opt-in: loaded only after "Accept".
//
// GTM is injected here, not by a Gatsby plugin. GATSBY_GTM_PARTYTOWN switches
// between main-thread and off-main-thread (Partytown) loading.

import React from "react"
import { Script, ScriptStrategy } from "gatsby"
import { useConsent } from "./consent-context"
import { CONSENT_GRANTED } from "./consent-cookie"

const GTM_ID = "GTM-WP7DL8PG"
const HUBSPOT_SRC = "https://js.hs-scripts.com/14572677.js"
const partytownEnabled = process.env.GATSBY_GTM_PARTYTOWN === "true"

// Off-main-thread GTM via Partytown. The snippet points at a same-origin path
// (/__gtm/gtm.js) that netlify.toml reverse-proxies to Google, because a web
// worker cannot fetch googletagmanager.com cross-origin. Mirror of the former
// gatsby-shared.js implementation.
const PartytownGtm = () => (
  <Script
    id="gtm-partytown"
    strategy={ScriptStrategy.offMainThread}
    forward={["dataLayer.push"]}
    dangerouslySetInnerHTML={{
      __html: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ platform: "gatsby" });
        (function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true;
          j.src = '/__gtm/gtm.js?id=' + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${GTM_ID}');
      `,
    }}
  />
)

// Classic main-thread GTM, loaded post-hydration (idle) since we can only inject
// it after the client-side consent check.
const StandardGtm = () => (
  <Script
    id="gtm"
    strategy={ScriptStrategy.idle}
    dangerouslySetInnerHTML={{
      __html: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ platform: "gatsby" });
        (function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true;
          j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${GTM_ID}');
      `,
    }}
  />
)

const TrackerScripts = () => {
  const { consent } = useConsent()

  return (
    <>
      {/* GTM loads for every visitor. The GA4/Ads tags inside it honor the
          Consent Mode v2 default (denied, set in <head>) and send cookieless
          pings until the visitor accepts — so refusals still show in GA4. */}
      {partytownEnabled ? <PartytownGtm /> : <StandardGtm />}
      {/* HubSpot sets its own cookies and is NOT a Consent Mode participant, so
          it stays strictly opt-in (loaded only after "Accept"). */}
      {consent === CONSENT_GRANTED && (
        <Script
          id="hs-script-loader"
          src={HUBSPOT_SRC}
          strategy={ScriptStrategy.idle}
        />
      )}
    </>
  )
}

export default TrackerScripts
